// (git-push auto-deploy via hub-deploy-sandbox — pipeline test)
// NDIS invoice console (admin, mobile-first). Mitchell creates support-provider
// and supported-person profiles, builds an invoice from the official NDIS
// catalogue, and sends it to the provider for approval. Testing only.
// Auth: same-origin session cookie (Mitchell is signed in); falls back to a
// ?token= URL param if present.

import { API_BASE } from '../api';

interface CatItem { code: string; name: string; category?: string; unit?: string; price?: number | null; quotable?: boolean; provider_travel?: boolean; }
interface Profile {
  id: string; display_name: string;
  legal_name?: string | null; email?: string | null; phone?: string | null; abn?: string | null;
  bank_account_name?: string | null; bank_bsb?: string | null; bank_account_number?: string | null;
  ndis_number?: string | null; address?: string | null; billing_email?: string | null; plan_manager_email?: string | null;
  line_codes?: string[] | null; notes?: string | null; channel_id?: string | null;
}
interface Line { code: string; name: string; unit: string; date: string; qty: number; rate: number; gst: number; amount: number; }
interface Picked { code: string; name: string; unit: string; rate: number; travel: boolean; travelCode?: string; travelRate?: number; travelName?: string; }
interface ResolvedCode { code: string; name?: string; unit?: string; price?: number | null; provider_travel?: boolean; found?: boolean; }

// Provider travel - non-labour: there is ONE travel code per support CATEGORY +
// REGISTRATION GROUP (format AA_799_GGGG_..). It's derived per selected support
// from the catalogue (the NDIA price guide), never hardcoded — see stagePicked().
const TRAVEL_NAME = 'Provider travel - non-labour costs';
const round2 = (n: number): number => Math.round(n * 100) / 100;
interface Inv {
  id: string; invoice_number?: string; status: string; total: number;
  provider_id?: string | null; participant_id?: string | null;
  approval_token?: string | null; sent_at?: string | null; sent_to_email?: string | null;
  created_at?: string | null; updated_at?: string | null;
}
interface InvLine { code?: string; name?: string; unit?: string; date?: string; qty?: number; rate?: number; amount?: number; }
interface InvFull extends Inv {
  line_items?: InvLine[]; subtotal?: number; gst_total?: number;
  service_date_from?: string | null; service_date_to?: string | null; notes?: string | null;
}

export function isNdisConsolePath(): boolean {
  return /\/ndis\/console\/?$/.test(window.location.pathname);
}

function tok(): string {
  return new URLSearchParams(window.location.search).get('token') || '';
}
function url(p: string): string {
  const base = API_BASE + p;
  const t = tok();
  if (!t) return base;
  return base + (base.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(t);
}
async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(url(path), {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json();
  if (json.status !== 'ok') throw new Error(json.error || `${res.status}`);
  return json.data as T;
}
function esc(s: string): string { const d = document.createElement('div'); d.textContent = s ?? ''; return d.innerHTML; }
function money(n: number): string { return `$${(n || 0).toFixed(2)}`; }
function dmy(s: string | null | undefined): string {
  if (!s) return '';
  const m = String(s).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s);
}

const STYLES = `
.nc-body{margin:0;background:#0b0c10;color:#e8e8ea;font:15px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif;display:block !important;height:auto !important;min-height:100dvh;overflow-y:auto !important}
.nc-root{display:block !important;width:100% !important;height:auto !important;min-height:0 !important;overflow:visible !important;flex:none !important}
.nc-wrap{max-width:620px;margin:0 auto;padding:14px 14px 40px}
.nc-brand{font-weight:700;color:#9aa0ff;font-size:13px;margin-bottom:10px}
.nc-tabs{display:flex;gap:6px;margin-bottom:14px;position:sticky;top:0;background:#0b0c10;padding:6px 0;z-index:5}
.nc-tab{flex:1;text-align:center;padding:10px;border-radius:10px;background:#15171e;border:1px solid #262a36;font-weight:650;font-size:13px;cursor:pointer}
.nc-tab.on{background:#5865f2;border-color:#5865f2;color:#fff}
.nc-card{background:#15171e;border:1px solid #262a36;border-radius:13px;padding:15px 14px;margin-bottom:12px}
.nc-h{font-size:15px;font-weight:800;margin:0 0 10px}
.nc-input,.nc-select{width:100%;box-sizing:border-box;background:#0e1016;border:1px solid #2b303d;color:#e8e8ea;border-radius:9px;padding:11px 12px;font-size:16px;margin-bottom:8px}
.nc-label{font-size:12px;color:#9499a6;margin:2px 0}
.nc-btn{width:100%;border:0;border-radius:11px;padding:13px;font-size:15px;font-weight:750;cursor:pointer;background:#5865f2;color:#fff;margin-top:4px}
.nc-btn.sec{background:#2b303d;color:#e8e8ea}
.nc-btn.ok{background:#3ba55d}
.nc-btn:disabled{opacity:.5}
.nc-row{display:flex;justify-content:space-between;gap:10px;padding:9px 0;border-bottom:1px solid #20242f}
.nc-row:last-child{border-bottom:0}
.nc-muted{color:#9499a6;font-size:12px}
.nc-pill{font-size:11px;padding:3px 8px;border-radius:20px;background:#20242f;color:#c9cdd8;white-space:nowrap}
.nc-pill.sent{background:#143d28;color:#cdeedb}
.nc-pill.review{background:#3a2f12;color:#efe3c0}
.nc-pill.draft{background:#1d2433;color:#aab4cc}
.nc-pill.await{background:#2a2350;color:#c8c2ff}
.nc-pill.reject{background:#3a1f1f;color:#ff9a9a}
.nc-inv{background:#15171e;border:1px solid #262a36;border-radius:13px;padding:13px 14px;margin-bottom:10px}
.nc-inv.stage{border-color:#5865f2;box-shadow:0 0 0 1px #5865f2 inset}
.nc-inv-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.nc-inv-head strong{font-size:15px}
.nc-inv-meta{color:#9499a6;font-size:12px;margin-top:3px;line-height:1.55}
.nc-inv-tot{font-size:16px;font-weight:800;white-space:nowrap}
.nc-stage-note{display:flex;align-items:center;gap:7px;background:#191535;border:1px solid #2f2a55;color:#c8c2ff;border-radius:9px;padding:8px 10px;margin:10px 0 2px;font-size:12.5px;font-weight:600}
.nc-stage-note svg{flex:none}
.nc-inv-actions{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:11px}
.nc-inv-actions .nc-btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;margin-top:0}
.nc-rev-panel{margin-top:10px;border-top:1px solid #20242f;padding-top:10px}
.nc-res{font-size:13px;margin-top:8px}
.nc-cat-item{padding:10px;border:1px solid #2b303d;border-radius:9px;margin-bottom:6px;cursor:pointer}
.nc-cat-item:active{background:#1c1f29}
.nc-line{display:flex;justify-content:space-between;gap:8px;align-items:center;padding:8px 0;border-bottom:1px solid #20242f}
.nc-x{background:#3a1f1f;color:#ff9a9a;border:0;border-radius:7px;padding:6px 9px;font-size:13px;cursor:pointer}
.nc-2{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.nc-link{word-break:break-all;color:#9aa0ff;font-size:12px}
.nc-profile{background:#15171e;border:1px solid #262a36;border-radius:13px;padding:13px 14px;margin-bottom:10px}
.nc-profile-head{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:8px}
.nc-profile-head strong{font-size:15px}
.nc-kv{display:flex;justify-content:space-between;gap:12px;padding:5px 0;border-top:1px solid #20242f;font-size:13px}
.nc-kv span:first-child{color:#9499a6;white-space:nowrap}
.nc-kv span:last-child{text-align:right;word-break:break-word}
.nc-del{background:#3a1f1f;color:#ff9a9a;border:1px solid #5a2b2b;border-radius:8px;padding:7px 12px;font-size:13px;font-weight:650;cursor:pointer;white-space:nowrap}
.nc-del.armed{background:#b3261e;color:#fff;border-color:#b3261e}
.nc-input[type="date"]{color-scheme:dark}
`;

let tab: 'invoice' | 'invoices' | 'people' | 'reviews' = 'invoice';
let providers: Profile[] = [];
let participants: Profile[] = [];
let lines: Line[] = [];
let picked: Picked | null = null;

export async function renderNdisConsole(): Promise<void> {
  document.title = 'NDIS invoices · the hub ecosystems';
  // #app is normally display:flex (Discord shell) with overflow:hidden, which
  // clips this page so it can't scroll. Undo that, like the public setup page.
  document.body.className = 'nc-body';
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('nc-root');
  root.innerHTML = '';
  ['sidebar-scrim', 'modal-overlay', 'toast'].forEach((id) => { const el = document.getElementById(id); if (el) el.style.display = 'none'; });
  const style = document.createElement('style'); style.textContent = STYLES; document.head.appendChild(style);

  // Auth gate — the console is private. Only a signed-in Hub People user (valid
  // hub-core session) or an explicit ?token= may use it. Everyone else gets a
  // sign-in prompt instead of the console UI.
  if (!(await isConsoleAuthed())) { renderConsoleGate(root); return; }

  const wrap = document.createElement('div'); wrap.className = 'nc-wrap';
  wrap.innerHTML = `<div class="nc-brand">the hub ecosystems · NDIS invoices <span class="nc-muted">(test)</span></div>
    <div class="nc-tabs">
      <div class="nc-tab" data-t="invoice">New invoice</div>
      <div class="nc-tab" data-t="invoices">Invoices</div>
      <div class="nc-tab" data-t="people">People</div>
      <div class="nc-tab" data-t="reviews">Reviews</div>
    </div><div id="nc-body"></div>`;
  root.appendChild(wrap);
  wrap.querySelectorAll('.nc-tab').forEach((el) => el.addEventListener('click', () => {
    tab = (el as HTMLElement).dataset.t as typeof tab; paint();
  }));
  try { providers = await api<{ items: Profile[] }>('GET', '/api/ndis/providers').then((d) => d.items); } catch { providers = []; }
  try { participants = await api<{ items: Profile[] }>('GET', '/api/ndis/participants').then((d) => d.items); } catch { participants = []; }
  paint();
}

// Signed in iff hub-core validates the session cookie (same check the main app
// uses at boot). `/api/auth/verify` is served by hub-core at the ROOT path, not
// hub-people — so it is NOT prefixed with API_BASE. An explicit ?token= (for
// programmatic access) also passes; the API still validates it on each call.
async function isConsoleAuthed(): Promise<boolean> {
  if (tok()) return true;
  try {
    const res = await fetch('/api/auth/verify', { credentials: 'include' });
    if (!res.ok) return false;
    const j = await res.json();
    return Boolean(j?.data?.identity_id);
  } catch { return false; }
}

function renderConsoleGate(root: HTMLElement): void {
  const wrap = document.createElement('div'); wrap.className = 'nc-wrap';
  wrap.innerHTML = `<div class="nc-brand">the hub ecosystems · NDIS invoices</div>
    <div class="nc-card">
      <div class="nc-h">Sign in required</div>
      <p class="nc-muted" style="margin:0 0 14px">This console is private. Sign in to Hub People to manage providers, supported people, and invoices.</p>
      <a class="nc-btn" href="/people/" style="display:block;text-align:center;text-decoration:none;box-sizing:border-box">Go to Hub People to sign in</a>
    </div>`;
  root.appendChild(wrap);
}

function paint(): void {
  document.querySelectorAll('.nc-tab').forEach((el) => el.classList.toggle('on', (el as HTMLElement).dataset.t === tab));
  const body = document.getElementById('nc-body') as HTMLElement;
  if (tab === 'invoice') return paintInvoice(body);
  if (tab === 'invoices') { void paintInvoices(body); return; }
  if (tab === 'people') return paintPeople(body);
  return paintReviews(body);
}

function paintInvoice(body: HTMLElement): void {
  const provOpts = providers.map((p) => `<option value="${esc(p.id)}">${esc(p.display_name)}</option>`).join('');
  const partOpts = participants.map((p) => `<option value="${esc(p.id)}">${esc(p.display_name)}</option>`).join('');
  const total = lines.reduce((s, l) => s + l.amount, 0);
  body.innerHTML = `
    <div class="nc-card">
      <div class="nc-h">Who</div>
      <div class="nc-label">Support provider</div>
      <select class="nc-select" id="nc-prov">${provOpts || '<option value="">— add a provider first —</option>'}</select>
      <div class="nc-label">Supported person</div>
      <select class="nc-select" id="nc-part">${partOpts || '<option value="">— add a participant first —</option>'}</select>
      <div class="nc-label">Invoice number</div>
      <input class="nc-input" id="nc-invnum" placeholder="Auto (e.g. 001) — or type your own">
    </div>
    <div class="nc-card">
      <div class="nc-h">Add a support line</div>
      <div class="nc-label">Date of support</div>
      <input class="nc-input" type="date" id="nc-ldate">
      <div id="nc-pcodes"></div>
      <div class="nc-label">Support (search name or code)</div>
      <input class="nc-input" id="nc-search" placeholder="e.g. 04_103_0125_6_1 or 'social and rec'" autocomplete="off">
      <div id="nc-cat"></div>
      <div id="nc-picked"></div>
    </div>
    <div class="nc-card">
      <div class="nc-h">Lines</div>
      <div id="nc-lines"></div>
      <div class="nc-row"><strong>Total (GST-free)</strong><strong>${money(total)}</strong></div>
    </div>
    <button class="nc-btn ok" id="nc-create" ${lines.length && providers.length && participants.length ? '' : 'disabled'}>Create &amp; send for approval</button>
    <div id="nc-result"></div>`;

  const dateInput = body.querySelector('#nc-ldate') as HTMLInputElement;
  dateInput.value = new Date().toISOString().slice(0, 10);
  paintLines(body);
  paintPicked(body);
  renderPartCodes(body);
  body.querySelector('#nc-part')?.addEventListener('change', () => renderPartCodes(body));

  const search = body.querySelector('#nc-search') as HTMLInputElement;
  const catBox = body.querySelector('#nc-cat') as HTMLElement;
  let t: number | undefined;
  search.addEventListener('input', () => {
    window.clearTimeout(t);
    t = window.setTimeout(async () => {
      const q = search.value.trim();
      if (q.length < 2) { catBox.innerHTML = ''; return; }
      try {
        const d = await api<{ items: CatItem[] }>('GET', `/api/ndis/catalogue?q=${encodeURIComponent(q)}&limit=8`);
        catBox.innerHTML = d.items.map((it) => `<div class="nc-cat-item" data-code="${esc(it.code)}" data-name="${esc(it.name)}" data-unit="${esc(it.unit || '')}" data-price="${it.price ?? 0}" data-travel="${it.provider_travel ? '1' : '0'}">
          <div>${esc(it.name)}${it.provider_travel ? ' <span class="nc-pill">travel</span>' : ''}</div><div class="nc-muted">${esc(it.code)} · ${it.price != null ? money(it.price) + '/' + esc(it.unit || 'ea') : 'Quote required'}</div></div>`).join('');
        catBox.querySelectorAll('.nc-cat-item').forEach((el) => el.addEventListener('click', () => {
          const d2 = (el as HTMLElement).dataset;
          search.value = ''; catBox.innerHTML = '';
          void stagePicked(body, { code: d2.code!, name: d2.name!, unit: d2.unit || '', price: parseFloat(d2.price || '0') || 0, provider_travel: d2.travel === '1' });
        }));
      } catch { catBox.innerHTML = '<div class="nc-muted">Search failed.</div>'; }
    }, 250);
  });

  body.querySelector('#nc-create')?.addEventListener('click', async () => {
    const btn = body.querySelector('#nc-create') as HTMLButtonElement;
    btn.disabled = true; btn.textContent = 'Creating…';
    const provider_id = (body.querySelector('#nc-prov') as HTMLSelectElement).value;
    const participant_id = (body.querySelector('#nc-part') as HTMLSelectElement).value;
    const invoice_number = (body.querySelector('#nc-invnum') as HTMLInputElement).value.trim();
    const dates = lines.map((l) => l.date).filter(Boolean).sort();
    const service_date_from = dates[0] || '';
    const service_date_to = dates[dates.length - 1] || '';
    try {
      const inv = await api<Inv>('POST', '/api/ndis/invoices', { provider_id, participant_id, invoice_number: invoice_number || undefined, line_items: lines, service_date_from, service_date_to });
      const r = await api<{ approval_link: string; intended_to: string }>('POST', `/api/ndis/invoices/${inv.id}/send-approval`);
      lines = []; picked = null;
      (body.querySelector('#nc-result') as HTMLElement).innerHTML = `<div class="nc-card"><div class="nc-h">Sent</div>
        <div class="nc-res">Invoice <strong>${esc(inv.invoice_number || inv.id)}</strong> created and the approval link was emailed to <strong>${esc(r.intended_to)}</strong> for review.</div>
        <div class="nc-label" style="margin-top:8px">Approval link</div><div class="nc-link">${esc(r.approval_link)}</div></div>`;
    } catch (e) {
      (body.querySelector('#nc-result') as HTMLElement).innerHTML = `<div class="nc-card nc-res">Error: ${esc((e as Error).message)}</div>`;
      btn.disabled = false; btn.textContent = 'Create & send for approval';
    }
  });
}

// Stage a catalogue item as the line being built. If it's travel-eligible, derive
// its matching travel code (AA_799_GGGG) from the catalogue and attach it.
async function stagePicked(body: HTMLElement, it: { code: string; name: string; unit?: string; price?: number | null; provider_travel?: boolean }): Promise<void> {
  picked = { code: it.code, name: it.name, unit: it.unit || '', rate: it.price ?? 0, travel: Boolean(it.provider_travel) };
  paintPicked(body);
  if (!picked.travel) return;
  const parts = it.code.split('_');
  if (parts.length < 3) return;
  const prefix = `${parts[0]}_799_${parts[2]}`; // category + reg group → travel code prefix
  try {
    const d = await api<{ items: CatItem[] }>('GET', `/api/ndis/catalogue?q=${encodeURIComponent(prefix)}&limit=10`);
    const tv = d.items.find((x) => x.code.startsWith(`${prefix}_`));
    if (tv && picked && picked.code === it.code) {
      picked.travelCode = tv.code; picked.travelRate = tv.price ?? 0; picked.travelName = tv.name;
      paintPicked(body);
    }
  } catch { /* no travel code resolved → km box stays hidden */ }
}

// Render the staged line being built (selected code + qty + optional travel km).
function paintPicked(body: HTMLElement): void {
  const box = body.querySelector('#nc-picked') as HTMLElement;
  if (!box) return;
  if (!picked) { box.innerHTML = ''; return; }
  const p = picked;
  const tRate = p.travelRate ?? 0;
  const travelNote = p.travel
    ? (p.travelCode
        ? `<div class="nc-muted" style="margin:-4px 0 8px">Adds a separate "${esc(p.travelName || TRAVEL_NAME)}" line (${esc(p.travelCode)}) at ${money(tRate)}/km.</div>`
        : `<div class="nc-muted" style="margin:-4px 0 8px">Looking up this support's travel code…</div>`)
    : '';
  box.innerHTML = `
    <div class="nc-card" style="margin:8px 0 0;background:#0e1016">
      <div><strong>${esc(p.name)}</strong></div>
      <div class="nc-muted" style="margin-bottom:8px">${esc(p.code)} · ${money(p.rate)}/${esc(p.unit || 'ea')}</div>
      <div class="nc-2">
        <div><div class="nc-label">Qty (${esc(p.unit || 'units')})</div><input class="nc-input" id="nc-qty" type="number" inputmode="decimal" min="0" step="0.25" value="1"></div>
        ${p.travel && p.travelCode ? `<div><div class="nc-label">Travel (km, optional)</div><input class="nc-input" id="nc-km" type="number" inputmode="decimal" min="0" step="1" placeholder="0"></div>` : ''}
      </div>
      ${travelNote}
      <div class="nc-2">
        <button class="nc-btn" id="nc-addline">Add line</button>
        <button class="nc-btn sec" id="nc-cancelpick">Cancel</button>
      </div>
    </div>`;
  box.querySelector('#nc-cancelpick')?.addEventListener('click', () => { picked = null; paintPicked(body); });
  box.querySelector('#nc-addline')?.addEventListener('click', () => {
    const date = (body.querySelector('#nc-ldate') as HTMLInputElement).value;
    const qty = parseFloat((body.querySelector('#nc-qty') as HTMLInputElement).value) || 0;
    if (qty <= 0) return;
    lines.push({ code: p.code, name: p.name, unit: p.unit, date, qty, rate: p.rate, gst: 0, amount: round2(qty * p.rate) });
    if (p.travel && p.travelCode) {
      const km = parseFloat((body.querySelector('#nc-km') as HTMLInputElement)?.value || '0') || 0;
      if (km > 0) {
        const r = p.travelRate ?? 0;
        lines.push({ code: p.travelCode, name: p.travelName || TRAVEL_NAME, unit: 'km', date, qty: km, rate: r, gst: 0, amount: round2(km * r) });
      }
    }
    picked = null;
    paintLines(body); paintPicked(body); refreshTotal(body);
  });
}

function paintLines(body: HTMLElement): void {
  const box = body.querySelector('#nc-lines') as HTMLElement;
  if (!box) return;
  box.innerHTML = lines.length
    ? lines.map((l, i) => `<div class="nc-line"><div><div>${esc(l.name)}</div><div class="nc-muted">${l.date ? esc(dmy(l.date)) + ' · ' : ''}${esc(l.code)} · ${l.qty} × ${money(l.rate)}</div></div><div style="display:flex;gap:8px;align-items:center"><strong>${money(l.amount)}</strong><button class="nc-x" data-rm="${i}">✕</button></div></div>`).join('')
    : '<div class="nc-muted">No lines yet — pick a date and search a support above.</div>';
  box.querySelectorAll('[data-rm]').forEach((el) => el.addEventListener('click', () => {
    lines.splice(parseInt((el as HTMLElement).dataset.rm!, 10), 1);
    paintLines(body); refreshTotal(body);
  }));
}

function refreshTotal(body: HTMLElement): void {
  const total = lines.reduce((s, l) => s + l.amount, 0);
  const row = body.querySelector('#nc-lines')?.parentElement?.querySelector('.nc-row strong:last-child') as HTMLElement | null;
  if (row) row.textContent = money(total);
  const create = body.querySelector('#nc-create') as HTMLButtonElement | null;
  if (create) create.disabled = !(lines.length && providers.length && participants.length);
}

// When a participant with linked codes is selected, offer a quick-pick dropdown
// of just their codes, each vetted against the catalogue (shows the description).
async function renderPartCodes(body: HTMLElement): Promise<void> {
  const box = body.querySelector('#nc-pcodes') as HTMLElement | null;
  if (!box) return;
  const pid = (body.querySelector('#nc-part') as HTMLSelectElement | null)?.value || '';
  const p = participants.find((x) => x.id === pid);
  const codes = p?.line_codes || [];
  if (!codes.length) { box.innerHTML = ''; return; }
  let resolved: ResolvedCode[] = [];
  try { resolved = (await api<{ items: ResolvedCode[] }>('POST', '/api/ndis/catalogue/resolve', { codes })).items; } catch { resolved = codes.map((c) => ({ code: c })); }
  if (!box.isConnected) return;
  box.innerHTML = `<div class="nc-label">This participant's codes</div>
    <select class="nc-select" id="nc-codepick"><option value="">— pick a linked code —</option>${resolved.map((it) => `<option value="${esc(it.code)}">${esc(it.code)}${it.found && it.name ? ' — ' + esc(it.name) : ' (not in catalogue)'}</option>`).join('')}</select>`;
  box.querySelector('#nc-codepick')?.addEventListener('change', (e) => {
    const code = (e.target as HTMLSelectElement).value;
    if (!code) return;
    const it = resolved.find((x) => x.code === code);
    if (it && it.found && it.name) { void stagePicked(body, { code: it.code, name: it.name, unit: it.unit, price: it.price, provider_travel: it.provider_travel }); }
  });
}

function paintPeople(body: HTMLElement): void {
  const setupLink = `${window.location.origin}/people/support-profile/setup`;
  const parseCodes = (text: string): string[] => text.split(/[\r\n,]+/).map((c) => c.trim()).filter(Boolean);
  const kv = (k: string, v: string | null | undefined): string => v ? `<div class="nc-kv"><span>${esc(k)}</span><span>${esc(v)}</span></div>` : '';
  const providerCard = (p: Profile): string => {
    const payTo = [p.bank_account_name, p.bank_bsb ? 'BSB ' + p.bank_bsb : '', p.bank_account_number ? 'Acc ' + p.bank_account_number : ''].filter(Boolean).join(' · ');
    return `<div class="nc-profile">
      <div class="nc-profile-head"><strong>${esc(p.display_name)}</strong><button class="nc-del" data-del-prov="${esc(p.id)}">Delete</button></div>
      ${p.legal_name && p.legal_name !== p.display_name ? kv('Legal name', p.legal_name) : ''}${kv('ABN', p.abn)}${kv('Email', p.email)}${kv('Phone', p.phone)}${payTo ? kv('Pay to', payTo) : ''}${p.line_codes && p.line_codes.length ? kv('Line codes', String(p.line_codes.length)) : ''}
    </div>`;
  };
  const participantCard = (p: Profile): string => `<div class="nc-profile">
      <div class="nc-profile-head"><strong>${esc(p.display_name)}</strong><span style="display:flex;gap:6px"><button class="nc-del" data-edit-part="${esc(p.id)}" style="background:#3a4150;color:#fff">Edit</button><button class="nc-del" data-del-part="${esc(p.id)}">Delete</button></span></div>
      ${kv('NDIS number', p.ndis_number)}${kv('Address', p.address)}${kv('Billing email', p.billing_email || p.plan_manager_email)}${p.line_codes && p.line_codes.length ? kv('Line codes', String(p.line_codes.length)) : ''}
      <div class="pe-edit" id="pe-${esc(p.id)}"></div>
    </div>`;

  body.innerHTML = `
    <div class="nc-card"><div class="nc-h">Invite a support worker</div>
      <p class="nc-muted" style="margin:0 0 10px">Send this link to a support worker. They enter their own details (legal name, ABN, bank, contact), review and submit — that creates their provider profile here, ready to invoice. No login needed.</p>
      <div class="nc-link" style="user-select:all;padding:9px 11px;border:1px solid #2b303d;border-radius:8px;background:#0e1016">${esc(setupLink)}</div>
      <div class="nc-2" style="margin-top:8px">
        <button class="nc-btn" id="nc-copy-setup">Copy link</button>
        <button class="nc-btn sec" id="nc-open-setup">Open page</button>
      </div></div>
    <div class="nc-card"><div class="nc-h">Add a participant</div>
      <div class="nc-label">Full name *</div>
      <input class="nc-input" id="pt-name" placeholder="e.g. Jordan Smith">
      <div class="nc-label">NDIS number *</div>
      <input class="nc-input" id="pt-ndis" inputmode="numeric" placeholder="e.g. 430000000">
      <div class="nc-label">Address</div>
      <input class="nc-input" id="pt-addr" placeholder="e.g. 1/32 Mallee St, Gateshead 2290">
      <div class="nc-label">Plan manager / billing email</div>
      <input class="nc-input" id="pt-bill" type="email" placeholder="billing@planmanager.com">
      <div class="nc-label">Line codes (optional — one per line or comma-separated)</div>
      <textarea class="nc-input" id="pt-codes" rows="3" placeholder="04_103_0125_6_1"></textarea>
      <button class="nc-btn" id="pt-add">Create participant</button>
      <div id="pt-res" class="nc-res"></div></div>
    <div class="nc-card"><div class="nc-h">Add a provider manually</div>
      <div class="nc-muted" style="margin:0 0 8px">Most providers self-set-up via the link above — use this for a quick manual add.</div>
      <input class="nc-input" id="pv-name" placeholder="Display name *">
      <input class="nc-input" id="pv-email" type="email" placeholder="Email (for approval link)">
      <input class="nc-input" id="pv-abn" placeholder="ABN">
      <button class="nc-btn" id="pv-add">Add provider</button>
      <div id="pv-res" class="nc-res"></div></div>
    <div class="nc-card"><div class="nc-h">Providers (${providers.length})</div>
      ${providers.length ? providers.map(providerCard).join('') : '<div class="nc-muted">None yet.</div>'}</div>
    <div class="nc-card"><div class="nc-h">Participants (${participants.length})</div>
      ${participants.length ? participants.map(participantCard).join('') : '<div class="nc-muted">None yet.</div>'}</div>
    <div class="nc-card"><div class="nc-h">Line codes for a participant</div>
      <div class="nc-label">Participant</div>
      <select class="nc-select" id="lc-part"><option value="">— select —</option>${participants.map((p) => `<option value="${esc(p.id)}">${esc(p.display_name)}</option>`).join('')}</select>
      <div class="nc-label">Paste line codes (one per line or comma-separated), or upload a CSV / TXT list</div>
      <textarea class="nc-input" id="lc-codes" rows="5" placeholder="01_011_0107_1_1&#10;04_104_0125_6_1"></textarea>
      <input type="file" id="lc-file" accept=".csv,.txt" class="nc-input">
      <button class="nc-btn" id="lc-save">Save line codes to participant</button>
      <div id="lc-res" class="nc-res"></div></div>`;
  body.querySelector('#nc-copy-setup')?.addEventListener('click', () => {
    const btn = body.querySelector('#nc-copy-setup') as HTMLButtonElement;
    navigator.clipboard?.writeText(setupLink)
      .then(() => { btn.textContent = 'Copied'; window.setTimeout(() => { btn.textContent = 'Copy link'; }, 1500); })
      .catch(() => { btn.textContent = 'Copy failed — select the link above'; });
  });
  body.querySelector('#nc-open-setup')?.addEventListener('click', () => { window.open(setupLink, '_blank'); });
  body.querySelector('#pt-add')?.addEventListener('click', async () => {
    const name = (body.querySelector('#pt-name') as HTMLInputElement).value.trim();
    const ndis = (body.querySelector('#pt-ndis') as HTMLInputElement).value.trim();
    const res = body.querySelector('#pt-res') as HTMLElement;
    if (!name || !ndis) { res.textContent = 'Full name and NDIS number are required.'; return; }
    const codes = parseCodes((body.querySelector('#pt-codes') as HTMLTextAreaElement).value);
    try {
      await api('POST', '/api/ndis/participants', {
        display_name: name, ndis_number: ndis,
        address: (body.querySelector('#pt-addr') as HTMLInputElement).value.trim(),
        billing_email: (body.querySelector('#pt-bill') as HTMLInputElement).value.trim(),
        billing_type: 'plan_managed',
        line_codes: codes.length ? codes : undefined,
      });
      participants = (await api<{ items: Profile[] }>('GET', '/api/ndis/participants')).items; paintPeople(body);
    } catch (e) { res.textContent = `Error: ${(e as Error).message}`; }
  });
  body.querySelector('#pv-add')?.addEventListener('click', async () => {
    const name = (body.querySelector('#pv-name') as HTMLInputElement).value.trim();
    const res = body.querySelector('#pv-res') as HTMLElement;
    if (!name) { res.textContent = 'Display name required.'; return; }
    try {
      await api('POST', '/api/ndis/providers', {
        display_name: name,
        email: (body.querySelector('#pv-email') as HTMLInputElement).value.trim(),
        abn: (body.querySelector('#pv-abn') as HTMLInputElement).value.trim(),
      });
      providers = (await api<{ items: Profile[] }>('GET', '/api/ndis/providers')).items; paintPeople(body);
    } catch (e) { res.textContent = `Error: ${(e as Error).message}`; }
  });
  const armDelete = (btn: HTMLButtonElement, run: () => Promise<void>): void => {
    btn.addEventListener('click', async () => {
      if (btn.dataset.armed !== '1') {
        btn.dataset.armed = '1'; btn.textContent = 'Tap again'; btn.classList.add('armed');
        window.setTimeout(() => { btn.dataset.armed = '0'; btn.textContent = 'Delete'; btn.classList.remove('armed'); }, 3000);
        return;
      }
      btn.textContent = 'Deleting…';
      try { await run(); } catch { btn.textContent = 'Failed'; }
    });
  };
  body.querySelectorAll('[data-del-prov]').forEach((el) => armDelete(el as HTMLButtonElement, async () => {
    await api('DELETE', `/api/ndis/providers/${(el as HTMLElement).dataset.delProv}`);
    providers = (await api<{ items: Profile[] }>('GET', '/api/ndis/providers')).items; paintPeople(body);
  }));
  body.querySelectorAll('[data-del-part]').forEach((el) => armDelete(el as HTMLButtonElement, async () => {
    await api('DELETE', `/api/ndis/participants/${(el as HTMLElement).dataset.delPart}`);
    participants = (await api<{ items: Profile[] }>('GET', '/api/ndis/participants')).items; paintPeople(body);
  }));
  // Edit full participant profile — loads the live record, edits in place, and
  // PUTs the FULL record back (round-trips so no field is dropped, same id so
  // any pending invoice stays linked).
  body.querySelectorAll('[data-edit-part]').forEach((el) => el.addEventListener('click', async () => {
    const pid = (el as HTMLElement).dataset.editPart!;
    const box = document.getElementById(`pe-${pid}`); if (!box) return;
    if (box.innerHTML.trim()) { box.innerHTML = ''; return; } // toggle closed
    box.innerHTML = '<div class="nc-muted" style="margin-top:8px">Loading…</div>';
    try {
      const full = await api<Record<string, unknown>>('GET', `/api/ndis/participants/${pid}`);
      const g = (k: string): string => { const v = full[k]; return v == null ? '' : String(v); };
      const codesStr = Array.isArray(full.line_codes) ? (full.line_codes as string[]).join('\n') : '';
      box.innerHTML = `<div style="margin-top:10px;border-top:1px solid #2b303d;padding-top:10px">
        <div class="nc-label">Full name *</div><input class="nc-input" data-f="display_name" value="${esc(g('display_name'))}">
        <div class="nc-label">NDIS number *</div><input class="nc-input" data-f="ndis_number" inputmode="numeric" value="${esc(g('ndis_number'))}">
        <div class="nc-label">Address</div><input class="nc-input" data-f="address" value="${esc(g('address'))}">
        <div class="nc-2"><div><div class="nc-label">Date of birth</div><input class="nc-input" type="date" data-f="dob" value="${esc(g('dob'))}"></div>
          <div><div class="nc-label">Billing type</div><input class="nc-input" data-f="billing_type" placeholder="plan_managed" value="${esc(g('billing_type'))}"></div></div>
        <div class="nc-2"><div><div class="nc-label">Plan start</div><input class="nc-input" type="date" data-f="plan_start" value="${esc(g('plan_start'))}"></div>
          <div><div class="nc-label">Plan end</div><input class="nc-input" type="date" data-f="plan_end" value="${esc(g('plan_end'))}"></div></div>
        <div class="nc-label">Plan manager name</div><input class="nc-input" data-f="plan_manager_name" value="${esc(g('plan_manager_name'))}">
        <div class="nc-label">Plan manager email</div><input class="nc-input" type="email" data-f="plan_manager_email" value="${esc(g('plan_manager_email'))}">
        <div class="nc-label">Billing email</div><input class="nc-input" type="email" data-f="billing_email" value="${esc(g('billing_email'))}">
        <div class="nc-label">Line codes (one per line or comma-separated)</div><textarea class="nc-input" rows="3" data-f="line_codes">${esc(codesStr)}</textarea>
        <div class="nc-label">Notes</div><textarea class="nc-input" rows="2" data-f="notes">${esc(g('notes'))}</textarea>
        <div class="nc-2"><button class="nc-btn ok" data-save-part="${esc(pid)}">Save changes</button><button class="nc-btn sec" data-cancel-part="${esc(pid)}">Cancel</button></div>
        <div class="nc-res" id="pe-res-${esc(pid)}"></div></div>`;
      box.querySelector('[data-cancel-part]')?.addEventListener('click', () => { box.innerHTML = ''; });
      box.querySelector('[data-save-part]')?.addEventListener('click', async () => {
        const get = (f: string): string => ((box.querySelector(`[data-f="${f}"]`) as HTMLInputElement | HTMLTextAreaElement | null)?.value || '').trim();
        const res = document.getElementById(`pe-res-${pid}`) as HTMLElement;
        const name = get('display_name'); const ndis = get('ndis_number');
        if (!name || !ndis) { res.textContent = 'Full name and NDIS number are required.'; return; }
        res.textContent = 'Saving…';
        try {
          await api('PUT', `/api/ndis/participants/${pid}`, {
            ...full,
            display_name: name, ndis_number: ndis, address: get('address'), dob: get('dob'),
            plan_start: get('plan_start'), plan_end: get('plan_end'), billing_type: get('billing_type'),
            plan_manager_name: get('plan_manager_name'), plan_manager_email: get('plan_manager_email'),
            billing_email: get('billing_email'), notes: get('notes'), line_codes: parseCodes(get('line_codes')),
          });
          participants = (await api<{ items: Profile[] }>('GET', '/api/ndis/participants')).items;
          paintPeople(body);
        } catch (e) { res.textContent = `Error: ${(e as Error).message}`; }
      });
    } catch (e) { box.innerHTML = `<div class="nc-res">Error: ${(e as Error).message}</div>`; }
  }));
  // Per-participant line-code list (paste or upload). Saved to the participant
  // profile without clobbering other fields (round-trips the full record).
  const lcPart = body.querySelector('#lc-part') as HTMLSelectElement;
  const lcCodes = body.querySelector('#lc-codes') as HTMLTextAreaElement;
  lcPart?.addEventListener('change', () => {
    const p = participants.find((x) => x.id === lcPart.value);
    lcCodes.value = (p?.line_codes || []).join('\n');
  });
  (body.querySelector('#lc-file') as HTMLInputElement)?.addEventListener('change', (e) => {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      const text = String(r.result || '');
      const codes = text.split(/[\r\n]+/).map((l) => l.split(',')[0].trim()).filter((c) => /^[0-9]/.test(c));
      lcCodes.value = codes.join('\n');
    };
    r.readAsText(f);
  });
  body.querySelector('#lc-save')?.addEventListener('click', async () => {
    const pid = lcPart.value;
    const res = body.querySelector('#lc-res') as HTMLElement;
    if (!pid) { res.textContent = 'Select a participant first.'; return; }
    const codes = parseCodes(lcCodes.value);
    res.textContent = 'Checking codes against the catalogue…';
    try {
      let unknown: string[] = [];
      if (codes.length) {
        const r = await api<{ items: ResolvedCode[] }>('POST', '/api/ndis/catalogue/resolve', { codes });
        unknown = r.items.filter((it) => !it.found).map((it) => it.code);
      }
      const full = await api<Record<string, unknown>>('GET', `/api/ndis/participants/${pid}`);
      await api('PUT', `/api/ndis/participants/${pid}`, { ...full, line_codes: codes });
      participants = (await api<{ items: Profile[] }>('GET', '/api/ndis/participants')).items;
      const msg = `Saved ${codes.length} code(s).${unknown.length ? ' Not in catalogue: ' + unknown.join(', ') : ' All valid.'}`;
      paintPeople(body);
      const r2 = body.querySelector('#lc-res') as HTMLElement | null;
      if (r2) r2.textContent = msg;
    } catch (err) {
      res.textContent = `Error: ${(err as Error).message}`;
    }
  });
}

// ─────────────────────────── Invoices list (review before send) ───────────────────────────
// Every invoice in one place so Mitchell can verify a corrected invoice in the
// console BEFORE the external approval email fires, and re-send a chosen one.
// "Review" opens the real PDF/approval page (token-gated) in a new tab when the
// invoice has been staged at least once, plus an inline line-item summary so a
// never-sent draft can still be checked with zero outbound side-effects.
// "Send for approval" hits POST /api/ndis/invoices/:id/send-approval behind an
// explicit two-tap confirm — nothing leaves without a deliberate click.

// Statuses the user is actively staging (corrected / not yet out the door).
const STAGING_STATUSES = new Set(['draft', 'edited_pending_review']);

const ICON_CLOCK = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path></svg>';
const ICON_EYE = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z"></path><circle cx="12" cy="12" r="3"></circle></svg>';
const ICON_SEND = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 2-7 20-4-9-9-4Z"></path><path d="M22 2 11 13"></path></svg>';

function statusPill(s: string): string {
  switch (s) {
    case 'sent': return '<span class="nc-pill sent">sent</span>';
    case 'sent_for_approval': return '<span class="nc-pill await">awaiting approval</span>';
    case 'edited_pending_review': return '<span class="nc-pill review">needs review</span>';
    case 'rejected': return '<span class="nc-pill reject">rejected</span>';
    case 'draft': return '<span class="nc-pill draft">draft</span>';
    default: return `<span class="nc-pill">${esc(s)}</span>`;
  }
}

function provName(id: string | null | undefined): string {
  return providers.find((p) => p.id === id)?.display_name || '—';
}
function partName(id: string | null | undefined): string {
  return participants.find((p) => p.id === id)?.display_name || '—';
}
// Public PDF/approval page for this invoice — token IS the credential. Mounted
// under /people (same convention as the support-profile setup link above).
function approveUrl(token: string): string {
  return `${window.location.origin}/people/ndis/approve/${encodeURIComponent(token)}`;
}

async function paintInvoices(body: HTMLElement): Promise<void> {
  body.innerHTML = `<div class="nc-card nc-muted">Loading invoices…</div>`;
  let invoices: Inv[] = [];
  try { invoices = (await api<{ items: Inv[] }>('GET', '/api/ndis/invoices')).items; }
  catch (e) { body.innerHTML = `<div class="nc-card nc-res">Error loading invoices: ${esc((e as Error).message)}</div>`; return; }

  const staging = invoices.filter((i) => STAGING_STATUSES.has(i.status));
  const card = (i: Inv): string => {
    const stage = STAGING_STATUSES.has(i.status);
    const sent = i.sent_at ? dmy(i.sent_at) : '';
    return `<div class="nc-inv${stage ? ' stage' : ''}">
      <div class="nc-inv-head">
        <div>
          <strong>${esc(i.invoice_number || i.id)}</strong>
          <div class="nc-inv-meta">
            ${esc(provName(i.provider_id))} &rarr; ${esc(partName(i.participant_id))}<br>
            ${sent ? `Sent ${esc(sent)}` : 'Not sent yet'}${i.sent_to_email ? ' · to ' + esc(i.sent_to_email) : ''}
          </div>
        </div>
        <div style="text-align:right;display:flex;flex-direction:column;gap:6px;align-items:flex-end">
          <span class="nc-inv-tot">${money(i.total)}</span>
          ${statusPill(i.status)}
        </div>
      </div>
      ${stage ? `<div class="nc-stage-note">${ICON_CLOCK}<span>Waiting to send — review before it goes out.</span></div>` : ''}
      <div class="nc-inv-actions">
        <button class="nc-btn sec" data-rev="${esc(i.id)}" data-tok="${esc(i.approval_token || '')}">${ICON_EYE} Review</button>
        <button class="nc-btn" data-send="${esc(i.id)}" data-label="${i.status === 'draft' ? 'Send for approval' : 'Re-send for approval'}">${ICON_SEND} ${i.status === 'draft' ? 'Send for approval' : 'Re-send for approval'}</button>
      </div>
      <div class="nc-rev-panel" id="iv-rev-${esc(i.id)}" style="display:none"></div>
    </div>`;
  };

  body.innerHTML = `
    ${staging.length ? `<div class="nc-card" style="border-color:#2f2a55">
      <div class="nc-h" style="display:flex;align-items:center;gap:8px">${ICON_CLOCK} Waiting to send (${staging.length})</div>
      <p class="nc-muted" style="margin:0">These invoices have been created or corrected but the approval email has not gone out. Review each one, then send it for approval with a deliberate click.</p>
    </div>` : ''}
    <div class="nc-card">
      <div class="nc-h">All invoices (${invoices.length})</div>
      ${invoices.length ? invoices.map(card).join('') : '<div class="nc-muted">No invoices yet. Create one from the “New invoice” tab.</div>'}
    </div>`;

  body.querySelectorAll('[data-rev]').forEach((el) => el.addEventListener('click', () =>
    toggleReview((el as HTMLElement).dataset.rev!, (el as HTMLElement).dataset.tok || '')));
  body.querySelectorAll('[data-send]').forEach((el) => el.addEventListener('click', () =>
    armSend(el as HTMLButtonElement, body)));
}

// Inline review: full line-item summary (zero outbound side-effects) plus a
// button to open the real PDF/approval page when the invoice has a token.
async function toggleReview(id: string, token: string): Promise<void> {
  const box = document.getElementById(`iv-rev-${id}`);
  if (!box) return;
  if (box.style.display !== 'none' && box.innerHTML.trim()) { box.style.display = 'none'; box.innerHTML = ''; return; }
  box.style.display = 'block';
  box.innerHTML = '<div class="nc-muted">Loading…</div>';
  try {
    const inv = await api<InvFull>('GET', `/api/ndis/invoices/${id}`);
    const items = inv.line_items || [];
    const rows = items.length
      ? items.map((l) => `<div class="nc-line"><div><div>${esc(l.name || l.code || 'Support')}</div><div class="nc-muted">${l.date ? esc(dmy(l.date)) + ' · ' : ''}${esc(l.code || '')} · ${l.qty ?? 0} × ${money(l.rate || 0)}</div></div><strong>${money(l.amount ?? (l.qty || 0) * (l.rate || 0))}</strong></div>`).join('')
      : '<div class="nc-muted">No line items.</div>';
    const pdfBtn = token
      ? `<button class="nc-btn sec" data-openpdf="${esc(token)}" style="margin-top:8px">Open PDF preview (approval page)</button>`
      : `<div class="nc-muted" style="margin-top:8px">PDF preview opens after the first send (the approval link is generated then). The summary above reflects exactly what will be sent.</div>`;
    box.innerHTML = `
      <div class="nc-muted" style="margin-bottom:6px">${esc(provName(inv.provider_id))} &rarr; ${esc(partName(inv.participant_id))}${inv.service_date_from ? ' · ' + esc(dmy(inv.service_date_from)) + (inv.service_date_to ? '–' + esc(dmy(inv.service_date_to)) : '') : ''}</div>
      ${rows}
      <div class="nc-row"><strong>Total (GST-free)</strong><strong>${money(inv.total)}</strong></div>
      ${pdfBtn}`;
    box.querySelector('[data-openpdf]')?.addEventListener('click', () => {
      window.open(approveUrl((box.querySelector('[data-openpdf]') as HTMLElement).dataset.openpdf!), '_blank', 'noopener');
    });
  } catch (e) { box.innerHTML = `<div class="nc-res">Error: ${esc((e as Error).message)}</div>`; }
}

// Two-tap confirm so the external email never fires on a single stray click.
function armSend(btn: HTMLButtonElement, body: HTMLElement): void {
  const id = btn.dataset.send!;
  const label = btn.dataset.label || 'Send for approval';
  if (btn.dataset.armed !== '1') {
    btn.dataset.armed = '1';
    btn.classList.add('ok');
    btn.innerHTML = `${ICON_SEND} Confirm — email the approval link`;
    window.setTimeout(() => {
      if (btn.dataset.armed === '1') {
        btn.dataset.armed = '0'; btn.classList.remove('ok');
        btn.innerHTML = `${ICON_SEND} ${label}`;
      }
    }, 4000);
    return;
  }
  btn.dataset.armed = '0'; btn.disabled = true; btn.textContent = 'Sending…';
  void (async (): Promise<void> => {
    try {
      const r = await api<{ approval_link: string; intended_to: string }>('POST', `/api/ndis/invoices/${id}/send-approval`);
      const box = document.getElementById(`iv-rev-${id}`);
      if (box) {
        box.style.display = 'block';
        box.innerHTML = `<div class="nc-res">Approval link emailed to <strong>${esc(r.intended_to)}</strong>.</div><div class="nc-link" style="margin-top:6px">${esc(r.approval_link)}</div>`;
      }
      await paintInvoices(body); // refresh statuses/sent dates
    } catch (e) {
      btn.disabled = false; btn.classList.remove('ok');
      btn.innerHTML = `${ICON_SEND} ${label}`;
      const box = document.getElementById(`iv-rev-${id}`);
      if (box) { box.style.display = 'block'; box.innerHTML = `<div class="nc-res">Error: ${esc((e as Error).message)}</div>`; }
    }
  })();
}

interface DLine { code?: string; name?: string; date?: string; qty?: number; rate?: number; amount?: number }
function dLineDesc(l: DLine): string { return `${l.qty ?? 0} × ${money(l.rate || 0)} = ${money(l.amount || 0)}`; }
function dKey(l: DLine): string { return `${l.code || ''}|${l.date || ''}`; }

// Diff "what was sent" (snapshot logged at send-for-approval) vs the provider's
// current line items: shows added / removed / changed / unchanged.
function renderInvoiceDiff(sent: DLine[], cur: DLine[], total: number): string {
  if (!sent.length) {
    const rows = cur.map((l) => `<div class="nc-line"><div><div>${esc(l.name || '')}</div><div class="nc-muted">${l.date ? esc(dmy(l.date)) + ' · ' : ''}${esc(l.code || '')} · ${dLineDesc(l)}</div></div></div>`).join('') || '<div class="nc-muted">No lines.</div>';
    return rows + `<div class="nc-row"><strong>Total</strong><strong>${money(total)}</strong></div><div class="nc-muted" style="margin-top:6px">No "sent" snapshot to compare — shown as-is.</div>`;
  }
  const sMap = new Map(sent.map((l) => [dKey(l), l] as const));
  const cMap = new Map(cur.map((l) => [dKey(l), l] as const));
  const keys = Array.from(new Set([...sMap.keys(), ...cMap.keys()]));
  let changed = 0;
  const rows = keys.map((k) => {
    const s = sMap.get(k); const c = cMap.get(k); const o = (c || s) as DLine;
    const head = `<div>${esc(o.name || '')}</div><div class="nc-muted">${o.date ? esc(dmy(o.date)) + ' · ' : ''}${esc(o.code || '')}</div>`;
    if (s && !c) { changed++; return `<div class="nc-line" style="border-left:3px solid #b3261e;padding-left:8px"><div>${head}<div style="color:#ff9a9a">Removed by provider — was ${dLineDesc(s)}</div></div></div>`; }
    if (c && !s) { changed++; return `<div class="nc-line" style="border-left:3px solid #3ba55d;padding-left:8px"><div>${head}<div style="color:#9ae6b4">Added by provider — ${dLineDesc(c)}</div></div></div>`; }
    if (s && c && ((s.qty ?? 0) !== (c.qty ?? 0) || round2(s.rate || 0) !== round2(c.rate || 0) || round2(s.amount || 0) !== round2(c.amount || 0))) {
      changed++;
      return `<div class="nc-line" style="border-left:3px solid #d9a23b;padding-left:8px"><div>${head}<div style="color:#efe3c0">Changed: <span style="color:#9499a6;text-decoration:line-through">${dLineDesc(s)}</span> → ${dLineDesc(c)}</div></div></div>`;
    }
    return `<div class="nc-line"><div>${head}<div class="nc-muted">${dLineDesc((c || s) as DLine)} · unchanged</div></div></div>`;
  }).join('');
  const summary = `<div class="nc-muted" style="margin:2px 0 8px">${changed ? `${changed} line(s) changed by the provider:` : 'No line changes — provider approved as sent.'}</div>`;
  return summary + rows + `<div class="nc-row"><strong>Total now</strong><strong>${money(total)}</strong></div>`;
}

async function paintReviews(body: HTMLElement): Promise<void> {
  body.innerHTML = `<div class="nc-card nc-muted">Loading…</div>`;
  let invoices: Inv[] = [];
  let changes: Array<{ id: string; provider_id: string; changes: Record<string, unknown> }> = [];
  try { invoices = (await api<{ items: Inv[] }>('GET', '/api/ndis/invoices')).items; } catch { /* ignore */ }
  try { changes = (await api<{ items: typeof changes }>('GET', '/api/ndis/profile-changes')).items; } catch { /* ignore */ }
  const pill = (s: string): string => s === 'sent' ? '<span class="nc-pill sent">sent</span>' : s === 'edited_pending_review' ? '<span class="nc-pill review">needs review</span>' : `<span class="nc-pill">${esc(s)}</span>`;
  const toReview = invoices.filter((i) => i.status === 'edited_pending_review');
  body.innerHTML = `
    <div class="nc-card"><div class="nc-h">Invoices to review (${toReview.length})</div>
      ${toReview.length ? toReview.map((i) => `<div class="nc-profile">
        <div class="nc-profile-head"><strong>${esc(i.invoice_number || i.id)} · ${money(i.total)}</strong>${pill(i.status)}</div>
        <div class="nc-muted" style="margin-bottom:8px">A provider edited this instead of approving. Review the changes, then re-send for their approval.</div>
        <div id="rv-${esc(i.id)}"></div>
        <div class="nc-2">
          <button class="nc-btn sec" data-view="${esc(i.id)}">View edited lines</button>
          <button class="nc-btn" data-resend="${esc(i.id)}">Re-send for approval</button>
        </div></div>`).join('') : '<div class="nc-muted">Nothing awaiting review.</div>'}</div>
    <div class="nc-card"><div class="nc-h">Pending provider profile edits</div>
      ${changes.length ? changes.map((c) => `<div class="nc-row"><span class="nc-muted">${esc(JSON.stringify(c.changes))}</span><button class="nc-btn sec" style="width:auto;padding:6px 12px;margin:0" data-apply="${esc(c.id)}">Apply</button></div>`).join('') : '<div class="nc-muted">Nothing to review.</div>'}</div>
    <div class="nc-card"><div class="nc-h">All invoices</div>
      ${invoices.length ? invoices.map((i) => `<div class="nc-row"><span>${esc(i.invoice_number || i.id)} · ${money(i.total)}</span>${pill(i.status)}</div>`).join('') : '<div class="nc-muted">No invoices yet.</div>'}</div>`;
  body.querySelectorAll('[data-view]').forEach((el) => el.addEventListener('click', async () => {
    const id = (el as HTMLElement).dataset.view!;
    const box = document.getElementById(`rv-${id}`); if (!box) return;
    box.innerHTML = '<div class="nc-muted">Loading…</div>';
    try {
      const [inv, snap] = await Promise.all([
        api<{ line_items?: DLine[]; total?: number }>('GET', `/api/ndis/invoices/${id}`),
        api<{ line_items?: DLine[] }>('GET', `/api/ndis/invoices/${id}/sent`).catch(() => ({ line_items: [] as DLine[] })),
      ]);
      box.innerHTML = renderInvoiceDiff(snap.line_items || [], inv.line_items || [], inv.total || 0);
    } catch (e) { box.innerHTML = `<div class="nc-res">Error: ${esc((e as Error).message)}</div>`; }
  }));
  body.querySelectorAll('[data-resend]').forEach((el) => el.addEventListener('click', async () => {
    const id = (el as HTMLElement).dataset.resend!;
    const box = document.getElementById(`rv-${id}`); if (!box) return;
    box.innerHTML = '<div class="nc-muted">Re-sending…</div>';
    try {
      const r = await api<{ approval_link: string; intended_to: string }>('POST', `/api/ndis/invoices/${id}/send-approval`);
      box.innerHTML = `<div class="nc-res">Re-sent to <strong>${esc(r.intended_to)}</strong> for approval.</div><div class="nc-link">${esc(r.approval_link)}</div>`;
    } catch (e) { box.innerHTML = `<div class="nc-res">Error: ${esc((e as Error).message)}</div>`; }
  }));
  body.querySelectorAll('[data-apply]').forEach((el) => el.addEventListener('click', async () => {
    await api('POST', `/api/ndis/profile-changes/${(el as HTMLElement).dataset.apply}/apply`);
    paintReviews(body);
  }));
}
